import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Cpu,
  ListOrdered,
  Loader2,
  Send,
  Sliders,
  Sparkles,
  Square,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CodeHighlighter from '@/components/code/SyntaxHighlighter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { cleanMarkdownBold } from '@/lib/utils';
import { TrainingService } from '@/services/TrainingService';
import type { TrainingJob } from '@/types/training';

type ChatMessage = { role: 'user' | 'assistant' | 'system'; content: string };

const DEFAULT_SYSTEM_PROMPT =
  'You are a helpful assistant fine-tuned by the user on their own corpus.';

function systemPromptFor(job: TrainingJob | null): string {
  const trained = (job?.metrics as any)?.training_system_prompt;
  return typeof trained === 'string' && trained.trim() ? trained : DEFAULT_SYSTEM_PROMPT;
}

function inferCodeLanguage(content: string): string {
  const text = content.trim();

  if (/^\s*#\s*include\s*[<"]/m.test(text) || /\bstd::\b/.test(text)) return 'cpp';
  if (/^\s*using\s+System\s*;/m.test(text) || /\bnamespace\s+\w+\s*\{/m.test(text)) return 'csharp';
  if (/^\s*package\s+main\b/m.test(text) || /\bfunc\s+main\s*\(/m.test(text)) return 'go';
  if (/^\s*(import|from)\s+[\w.]+\s+import\b/m.test(text) || /\bdef\s+\w+\s*\(/m.test(text)) return 'python';
  if (/\b(fn|let mut|impl|println!)\b/.test(text)) return 'rust';
  if (/\b(public\s+static\s+void\s+main|System\.out\.println)\b/.test(text)) return 'java';
  if (/<\/?[a-z][\s\S]*>/i.test(text)) return 'html';
  if (/\b(interface|type)\s+\w+\s*=|:\s*(string|number|boolean)\b/.test(text)) return 'typescript';
  if (/\b(function|const|let|=>|console\.log)\b/.test(text)) return 'javascript';
  if (/^\s*[\w.-]+\s*:\s*[^;]+;/m.test(text)) return 'css';
  if (/^\s*SELECT\s+.+\s+FROM\s+/im.test(text)) return 'sql';
  if (/^\s*[{[]/.test(text)) return 'json';
  if (/^\s*#!/.test(text) || /\b(echo|printf|grep|sed|awk)\b/.test(text)) return 'bash';

  return 'text';
}

function looksLikeStandaloneCode(content: string): boolean {
  const text = content.trim();
  if (!text || text.includes('```')) return false;

  // Hard-gate: prose with normal sentence punctuation almost never IS
  // code, even if it contains keywords like "return", "from", "function".
  // Multiple sentence-ending periods in the middle of text is a strong
  // signal we're looking at prose, not code.
  const sentenceEndings = (text.match(/[.!?]\s+[A-Z]/g) || []).length;
  if (sentenceEndings >= 2) return false;

  const lines = text.split('\n').filter((line) => line.trim());

  // Strict code signals — these must anchor at line start or appear in
  // syntactic positions that don't naturally arise in prose. Weakened
  // patterns like \b(return|from|function)\b were producing false
  // positives on natural-language responses.
  const codeSignals = [
    /^\s*#\s*include\s*[<"]/m,                            // C/C++ include
    /^\s*(int|void|auto|class|struct|enum)\s+\w+\s*[({]/m, // C/C++/Java decl
    /\bstd::\w+/,                                         // C++ stdlib
    /^\s*(function|const|let|var)\s+\w+\s*[=(]/m,         // JS at line start
    /^\s*(def|class)\s+\w+\s*[(:]/m,                      // Python at line start
    /^\s*(import|from)\s+[\w.]+/m,                        // import statement
    /^\s*return\s+[\w(\[{'"]/m,                           // explicit return at line start
    /^\s*(console\.log|print|printf|cout\s*<<)\s*\(/m,    // print call at line start
    /^[\s\w*]*\{\s*$/m,                                   // line ending in opening brace
    /;\s*$/m,                                             // trailing semicolon
  ];
  const signalCount = codeSignals.filter((pattern) => pattern.test(text)).length;

  // Require at least 2 strict signals for very short content, or 3 for
  // multi-line content. Single-signal triggers (which caused most of
  // the prose-as-code misfires) are gone.
  return signalCount >= 2 && (lines.length <= 6 || signalCount >= 3);
}

function stripMarkdownCodeFence(value: string): string {
  return value.replace(/\n$/, '');
}

function ChatCodeBlock({ code, language }: { code: string; language: string }) {
  const [copied, setCopied] = useState(false);
  const normalizedLanguage = language || inferCodeLanguage(code);
  const label = normalizedLanguage === 'text' ? 'code' : normalizedLanguage;

  const copyCode = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="not-prose my-3 overflow-hidden rounded-lg border border-border/70 bg-[#101014] shadow-sm">
      <div className="flex items-center justify-between border-b border-white/10 bg-white/[0.03] px-3 py-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-300">
          {label}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-slate-300 hover:bg-white/10 hover:text-white"
          onClick={() => void copyCode()}
        >
          {copied ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      {/* Inner wraps now (SyntaxHighlighter wrapLongLines + pre-wrap),
          so no outer horizontal scroll needed. */}
      <div className="text-[13px] leading-relaxed">
        <CodeHighlighter code={code} language={normalizedLanguage} />
      </div>
    </div>
  );
}

// Split assistant content into its <think> reasoning block (if any)
// and the final answer. Handles both forms:
//   "<think>reasoning</think>answer"   ← explicit opening tag
//   "reasoning</think>answer"          ← Qwen 3.5 forced-prefix mode,
//                                        chat template emits opening tag
//                                        as part of the assistant prefix
//                                        so the model only outputs the
//                                        closing tag.
// Returns {reasoning: string|null, answer: string}. If no </think> is
// found, the whole content is the answer.
function splitThinkBlock(content: string): { reasoning: string | null; answer: string } {
  const closeIdx = content.indexOf('</think>');
  if (closeIdx === -1) return { reasoning: null, answer: content };

  const before = content.slice(0, closeIdx);
  const after = content.slice(closeIdx + '</think>'.length);

  const openIdx = before.indexOf('<think>');
  const reasoning = openIdx === -1
    ? before.trim()  // implicit opening (Qwen 3.5 forced prefix)
    : before.slice(openIdx + '<think>'.length).trim();
  return { reasoning, answer: after.replace(/^\s+/, '') };
}

function ThinkBlock({ reasoning }: { reasoning: string }) {
  const [open, setOpen] = useState(false);
  return (
    <details
      className="not-prose mb-3 rounded-md border border-border/60 bg-muted/30 text-sm"
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {open ? 'Hide reasoning' : 'Show reasoning'}
      </summary>
      <div className="border-t border-border/40 px-3 py-2 font-mono text-[12px] leading-relaxed text-muted-foreground whitespace-pre-wrap break-words">
        {reasoning}
      </div>
    </details>
  );
}

// Post-process assistant content to fix identity-name drift. The Qwythos
// base has a strong "I am Qwythos" prior that even rank-256 LoRA can't
// fully override at sampling time — so we rewrite identity claims at the
// rendering layer. ONLY rewrites self-identification patterns
// ("I'm Qwythos", "My name is Qwythos") — leaves accurate base-model
// references intact ("Qwythos is my base model").
//
// The display name is the job's output_name with proper casing
// (canonical "MaplePT2-Reasoning" / "MaplePT2-Coder"). Pass empty
// string to disable.
function rewriteIdentity(text: string, displayName: string): string {
  if (!displayName || !text) return text;
  // LLMs often emit curly apostrophes (U+2019) instead of straight
  // ASCII apostrophes (U+0027) — chat templates use curly quotes by
  // convention. Match either form in identity patterns.
  const APO = "[''’]?";
  const rules: Array<[RegExp, string]> = [
    // Self-naming patterns — these are unambiguous identity claims
    [new RegExp(`\\bI${APO}m Qwythos\\b`, "g"), `I'm ${displayName}`],
    [/\bI am Qwythos\b/g, `I am ${displayName}`],
    [/\bMy name is Qwythos\b/g, `My name is ${displayName}`],
    [/\bThey call me Qwythos\b/g, `They call me ${displayName}`],
    [new RegExp(`\\bI${APO}m called Qwythos\\b`, "g"), `I'm called ${displayName}`],
    [/\bcall me Qwythos\b/g, `call me ${displayName}`],
    [/\bThis is Qwythos\b/g, `This is ${displayName}`],
    [/\bQwythos here\b/g, `${displayName} here`],
    // Family / lineage attribution where the model wrongly credits Empero
    // for the *family* (Empero made the base, but the family/line is ours)
    [/\bQwythos family\b/g, `MaplePT2 family`],
    [/\bQwythos lineage\b/g, `MaplePT2 lineage`],
    // "Empero AI's family/lineage/line" — direct ownership claim.
    // Also catch "Empero AI's <intermediate words> lineage" up to a few
    // words (e.g., "Empero AI's Qwen 3.5 lineage", "Empero AI's
    // Qwythos line"). Empero owns the BASE; the family/lineage of the
    // fine-tune belongs to CanXP AI.
    [new RegExp(`\\bEmpero AI${APO}s (family|lineage|line)\\b`, "g"), `CanXP AI's $1`],
    [new RegExp(`\\bEmpero AI${APO}s (?:[\\w.-]+\\s+){1,4}(family|lineage|line)\\b`, "g"), `CanXP AI's MaplePT2 $1`],
    // "Canadian-trained" or "Canadian-sovereign" attached to anyone but
    // CanXP AI is wrong — those are CanXP-specific descriptors. If the
    // model says "Empero AI's ... Canadian-trained models", strip the
    // false Empero attribution.
    [/\bEmpero AI('s|'s|s)?\s+(reasoning|Canadian-trained|Canadian-sovereign)/g, `CanXP AI's $2`],
  ];
  let out = text;
  for (const [re, replacement] of rules) {
    out = out.replace(re, replacement);
  }
  return out;
}

// Canonical display name from a job's output_name. HF stores lowercase
// (maplept2-reasoning) but the trained model identity uses CamelCase
// (MaplePT2-Reasoning). Special-case the known names.
function jobDisplayName(outputName: string | null | undefined): string {
  if (!outputName) return '';
  const lower = outputName.toLowerCase();
  if (lower.startsWith('maplept2-reasoning')) return 'MaplePT2-Reasoning';
  if (lower.startsWith('maplept2-coder')) return 'MaplePT2-Coder';
  if (lower.startsWith('maplept2')) return 'MaplePT2';
  if (lower.startsWith('maplept')) return 'MaplePT';
  return outputName;
}

function AssistantMarkdown({ content }: { content: string }) {
  const { reasoning, answer } = splitThinkBlock(content);

  // Whole response is just code — special case, no <think> processing.
  if (!reasoning && looksLikeStandaloneCode(content)) {
    return <ChatCodeBlock code={content.trim()} language={inferCodeLanguage(content)} />;
  }

  return (
    <>
      {reasoning ? <ThinkBlock reasoning={reasoning} /> : null}
      <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ ...props }) => (
          <a {...props} target="_blank" rel="noreferrer noopener" />
        ),
        code: ({ className, children, ...props }) => {
          const value = stripMarkdownCodeFence(String(children));
          const language = /language-([\w#+-]+)/.exec(className || '')?.[1] || inferCodeLanguage(value);
          const isInline = !className && !value.includes('\n');

          if (isInline) {
            return (
              <code
                {...props}
                className="rounded bg-muted px-1 py-0.5 font-mono text-[0.9em] text-foreground"
              >
                {children}
              </code>
            );
          }

          return <ChatCodeBlock code={value} language={language} />;
        },
        pre: ({ children }) => <>{children}</>,
      }}
    >
      {cleanMarkdownBold(answer)}
    </ReactMarkdown>
    </>
  );
}

export default function FinetuneChat() {
  const { jobId: routeJobId } = useParams();
  const [searchParams] = useSearchParams();
  const queryJobId = searchParams.get('job');
  const initialJobId = routeJobId || queryJobId || null;
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  const [models, setModels] = useState<TrainingJob[]>([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(initialJobId);
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: 'system', content: DEFAULT_SYSTEM_PROMPT },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [queuedMessages, setQueuedMessages] = useState<string[]>([]);
  const [queueExpanded, setQueueExpanded] = useState(true);
  const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const messageContentRefs = useRef(new Map<number, HTMLDivElement>());
  const activeRunIdRef = useRef(0);
  const pendingDelayRef = useRef<{ timeoutId: number; resolve: () => void } | null>(null);
  // AbortController for the in-flight streaming chat so a job switch
  // or unmount tears the upstream stream down instead of leaking it.
  const pendingAbortRef = useRef<AbortController | null>(null);

  const [temperature, setTemperature] = useState(0.7);
  const [repetitionPenalty, setRepetitionPenalty] = useState(1.15);
  const [maxTokens, setMaxTokens] = useState(4096);
  // Customer-supplied system prompt for the selected job. The CanXP
  // brand preamble is auto-prepended server-side; this is the
  // persona/scope text the user controls. Hydrated from the job row
  // when a model is selected; PUT-back when saved.
  const [systemPrompt, setSystemPrompt] = useState('');
  const [systemPromptSaving, setSystemPromptSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setModelsLoading(true);
      try {
        const res = await TrainingService.listJobs({ status: 'completed', limit: 100 });
        if (cancelled) return;
        const usable = res.jobs.filter(
          (j) => (j.metrics as any)?.huggingface?.repo,
        );
        setModels(usable);
        if (!selectedJobId && usable[0]) setSelectedJobId(usable[0].id);
      } catch (e: any) {
        toast({
          title: 'Could not load your trained models',
          description: String(e?.message || e),
          variant: 'destructive',
        });
      } finally {
        if (!cancelled) setModelsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, selectedJobId, toast]);

  const selectedModel = useMemo(
    () => models.find((m) => m.id === selectedJobId) || null,
    [models, selectedJobId],
  );

  // Sync the system prompt textarea to the selected job's stored value.
  useEffect(() => {
    setSystemPrompt(((selectedModel as any)?.system_prompt as string | null) ?? '');
  }, [selectedModel]);

  const saveSystemPrompt = useCallback(async () => {
    if (!selectedJobId) return;
    setSystemPromptSaving(true);
    try {
      const updated = await TrainingService.setSystemPrompt(
        selectedJobId,
        systemPrompt.trim() || null,
      );
      // Optimistically update local job list so the value persists
      // across re-renders without a refetch.
      setModels((prev) =>
        prev.map((m) => (m.id === selectedJobId ? { ...m, ...updated } : m)),
      );
      toast({
        title: 'System prompt saved',
        description: systemPrompt.trim()
          ? 'Applied to all future chat turns and API calls.'
          : 'Cleared — model now uses just the CanXP preamble.',
      });
    } catch (e: any) {
      toast({
        title: 'Could not save system prompt',
        description: String(e?.message || e),
        variant: 'destructive',
      });
    } finally {
      setSystemPromptSaving(false);
    }
  }, [selectedJobId, systemPrompt, toast]);

  useEffect(() => {
    if (!selectedModel) return;
    const expected = systemPromptFor(selectedModel);
    setMessages((prev) => {
      if (prev.length === 1 && prev[0].role === 'system' && prev[0].content !== expected) {
        return [{ role: 'system', content: expected }];
      }
      return prev;
    });
  }, [selectedModel]);

  const visibleMessages = messages.filter((m) => m.role !== 'system');

  useEffect(() => {
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea) return;

    const frame = window.requestAnimationFrame(() => {
      scrollArea.scrollTo({
        top: scrollArea.scrollHeight,
        behavior: visibleMessages.length > 1 ? 'smooth' : 'auto',
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [visibleMessages.length, sending, queuedMessages.length]);

  const resolvePendingDelay = useCallback(() => {
    const pendingDelay = pendingDelayRef.current;
    if (!pendingDelay) return;
    window.clearTimeout(pendingDelay.timeoutId);
    pendingDelayRef.current = null;
    pendingDelay.resolve();
  }, []);

  const stopActiveRun = useCallback(
    ({ clearQueue = true, showToast = true }: { clearQueue?: boolean; showToast?: boolean } = {}) => {
      const hadActiveWork = sending || queuedMessages.length > 0;
      activeRunIdRef.current += 1;
      resolvePendingDelay();
      // Tear down the upstream SSE so the pod-side generation stops
      // costing compute as soon as the user hits Stop.
      pendingAbortRef.current?.abort();
      pendingAbortRef.current = null;
      setSending(false);
      if (clearQueue) {
        setQueuedMessages([]);
      }
      if (showToast && hadActiveWork) {
        toast({
          title: 'Response stopped',
          description: clearQueue
            ? 'The current response and queued messages were cleared.'
            : 'The current response was stopped.',
        });
      }
    },
    [queuedMessages.length, resolvePendingDelay, sending, toast],
  );

  useEffect(() => () => {
    activeRunIdRef.current += 1;
    resolvePendingDelay();
    pendingAbortRef.current?.abort();
    pendingAbortRef.current = null;
  }, [resolvePendingDelay]);

  const copyMessage = async (content: string, index: number) => {
    const messageElement = messageContentRefs.current.get(index);
    const plainText = messageElement?.innerText || content;

    try {
      if (messageElement && 'ClipboardItem' in window && navigator.clipboard.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([messageElement.innerHTML], { type: 'text/html' }),
            'text/plain': new Blob([plainText], { type: 'text/plain' }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(plainText);
      }

      setCopiedMessageIndex(index);
      window.setTimeout(() => {
        setCopiedMessageIndex((current) => (current === index ? null : current));
      }, 2000);
    } catch (error: any) {
      try {
        await navigator.clipboard.writeText(plainText);
        setCopiedMessageIndex(index);
        window.setTimeout(() => {
          setCopiedMessageIndex((current) => (current === index ? null : current));
        }, 2000);
      } catch {
        toast({
          title: 'Copy failed',
          description: String(error?.message || error),
          variant: 'destructive',
        });
      }
    }
  };

  const runChatTurn = useCallback(
    async (text: string, fromQueue = false) => {
      if (!selectedJobId) return;

      const runId = activeRunIdRef.current + 1;
      activeRunIdRef.current = runId;

      const next = [...messages, { role: 'user' as const, content: text }];
      // Append both the user's message AND an empty assistant slot up
      // front so the streaming-delta appends have a stable index to
      // mutate. The user sees the assistant bubble appear immediately
      // and fill in as tokens arrive.
      setMessages([...next, { role: 'assistant' as const, content: '' }]);
      setSending(true);

      const abortController = new AbortController();
      pendingAbortRef.current = abortController;

      try {
        let accumulated = '';
        await TrainingService.streamChat(
          selectedJobId,
          {
            messages: next,
            max_tokens: maxTokens,
            temperature,
            repetition_penalty: repetitionPenalty,
          },
          (delta) => {
            if (activeRunIdRef.current !== runId) return;
            accumulated += delta;
            // Resolve displayName INSIDE the onDelta so we get the
            // current selectedModel (closure-captured value can be
            // null if models[] loaded after runChatTurn fired).
            // Multi-source fallback: model registry → URL → hardcoded
            // default for known MaplePT base. We never want a missing
            // displayName to silently disable the rewrite.
            const fromModel = jobDisplayName(selectedModel?.output_name);
            const fromUrl = jobDisplayName(
              window.location.pathname.includes('maplept2-reasoning')
                ? 'maplept2-reasoning' : '',
            );
            const displayName = fromModel || fromUrl || 'MaplePT2-Reasoning';
            const snapshot = rewriteIdentity(accumulated, displayName);
            setMessages((prev) => {
              if (prev.length === 0) return prev;
              const lastIdx = prev.length - 1;
              if (prev[lastIdx].role !== 'assistant') return prev;
              const out = prev.slice();
              out[lastIdx] = { role: 'assistant', content: snapshot };
              return out;
            });
          },
          abortController.signal,
        );

        if (activeRunIdRef.current !== runId) return;
        // Stream ended cleanly; the message is already in place. If
        // the stream produced zero tokens (rare — usually means the
        // model emitted just a stop sequence) leave the empty bubble
        // so the user sees something happened.
      } catch (e: any) {
        if (e?.name === 'AbortError') return;
        if (activeRunIdRef.current !== runId) return;

        toast({
          title: 'Chat failed',
          description: String(e?.message || e),
          variant: 'destructive',
        });

        // Pop BOTH the user echo and the empty assistant placeholder
        // we inserted before the stream started.
        setMessages((prev) => prev.slice(0, -2));
        setInput((current) => {
          if (current.trim()) {
            return fromQueue ? `${text}\n${current}` : current;
          }
          return text;
        });
        setQueuedMessages([]);
      } finally {
        if (pendingAbortRef.current === abortController) {
          pendingAbortRef.current = null;
        }
        if (activeRunIdRef.current === runId) {
          resolvePendingDelay();
          setSending(false);
        }
      }
    },
    [maxTokens, messages, repetitionPenalty, resolvePendingDelay, selectedJobId, temperature, toast],
  );

  useEffect(() => {
    if (sending || !selectedJobId || queuedMessages.length === 0) return;

    const [nextMessage, ...rest] = queuedMessages;
    setQueuedMessages(rest);
    void runChatTurn(nextMessage, true);
  }, [queuedMessages, sending, selectedJobId, runChatTurn]);

  const queueOrSendMessage = useCallback(() => {
    const text = input.trim();
    if (!text || !selectedJobId) return;

    setInput('');

    if (sending || queuedMessages.length > 0) {
      setQueuedMessages((prev) => [...prev, text]);
      setQueueExpanded(true);
      return;
    }

    void runChatTurn(text);
  }, [input, queuedMessages.length, runChatTurn, selectedJobId, sending]);

  const resetConversation = useCallback(() => {
    stopActiveRun({ clearQueue: true, showToast: false });
    setMessages([{ role: 'system', content: systemPromptFor(selectedModel) }]);
  }, [selectedModel, stopActiveRun]);

  const handleModelChange = useCallback(
    (jobId: string) => {
      const nextModel = models.find((model) => model.id === jobId) || null;
      stopActiveRun({ clearQueue: true, showToast: false });
      setSelectedJobId(jobId);
      setMessages([{ role: 'system', content: systemPromptFor(nextModel) }]);
      setInput('');
    },
    [models, stopActiveRun],
  );

  const queueCountLabel = queuedMessages.length === 1 ? '1 Queued' : `${queuedMessages.length} Queued`;

  return (
    <div className="mx-auto flex h-[calc(100vh-2rem)] w-full flex-col px-4 py-6 lg:px-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Back to dashboard
        </Button>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                System
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-96 space-y-3" align="end">
              <div className="space-y-1">
                <Label htmlFor="system_prompt" className="text-sm">System prompt</Label>
                <p className="text-xs text-muted-foreground">
                  Define your model's persona, scope, or guardrails. The
                  CanXP brand preamble is automatically prepended.
                </p>
                <textarea
                  id="system_prompt"
                  className="min-h-[140px] w-full rounded-md border bg-background px-3 py-2 font-mono text-xs leading-snug"
                  placeholder="e.g. You are a friendly assistant for ACME Corp. Be concise. Always cite sources."
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                />
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{systemPrompt.length} chars · saved to job, applies to API too</span>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void saveSystemPrompt()}
                  disabled={systemPromptSaving || !selectedJobId}
                >
                  {systemPromptSaving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <Sliders className="mr-1.5 h-4 w-4" />
                Sampling
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 space-y-4" align="end">
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label htmlFor="temperature" className="text-sm">Temperature</Label>
                  <span className="text-xs tabular-nums text-muted-foreground">{temperature.toFixed(2)}</span>
                </div>
                <Input
                  id="temperature"
                  type="range"
                  min={0}
                  max={2}
                  step={0.05}
                  value={temperature}
                  onChange={(e) => setTemperature(Number(e.target.value))}
                />
                <p className="text-xs text-muted-foreground">
                  Higher = more varied. 0 = deterministic.
                </p>
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label htmlFor="repetition_penalty" className="text-sm">Repetition penalty</Label>
                  <span className="text-xs tabular-nums text-muted-foreground">{repetitionPenalty.toFixed(2)}</span>
                </div>
                <Input
                  id="repetition_penalty"
                  type="range"
                  min={1}
                  max={2}
                  step={0.05}
                  value={repetitionPenalty}
                  onChange={(e) => setRepetitionPenalty(Number(e.target.value))}
                />
                <p className="text-xs text-muted-foreground">
                  1.0 = off. 1.15 default. Raise toward 1.3 if outputs loop.
                </p>
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label htmlFor="max_tokens" className="text-sm">Max output tokens</Label>
                  <span className="text-xs tabular-nums text-muted-foreground">{maxTokens}</span>
                </div>
                <Input
                  id="max_tokens"
                  type="range"
                  min={64}
                  max={8192}
                  step={64}
                  value={maxTokens}
                  onChange={(e) => setMaxTokens(Number(e.target.value))}
                />
                <p className="text-xs text-muted-foreground">
                  Hard ceiling on the reply length.
                </p>
              </div>
            </PopoverContent>
          </Popover>
          <Button
            variant="outline"
            size="sm"
            onClick={resetConversation}
            disabled={visibleMessages.length === 0 && queuedMessages.length === 0 && !sending}
          >
            Reset conversation
          </Button>
        </div>
      </div>

      <Card className="flex min-h-0 flex-1 flex-col">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-canada-red" />
            Chat with your fine-tuned model
          </CardTitle>
          <div className="pt-2">
            <Select
              value={selectedJobId || ''}
              onValueChange={handleModelChange}
              disabled={modelsLoading || models.length === 0}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={
                    modelsLoading
                      ? 'Loading your fine-tunes…'
                      : models.length === 0
                        ? 'No fine-tunes published to HuggingFace yet'
                        : 'Pick a fine-tune to chat with'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {models.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{m.output_name}</span>
                      <span className="text-xs text-muted-foreground">
                        · {m.base_model.split('/').pop()}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedModel && (
              <p className="mt-2 text-xs text-muted-foreground">
                <Cpu className="mr-1 inline h-3 w-3" />
                Adapter: {(selectedModel.metrics as any)?.huggingface?.repo} —
                cold-start can take 30–60 seconds; subsequent messages are fast.
              </p>
            )}
          </div>
        </CardHeader>

        <CardContent className="flex min-h-0 flex-1 flex-col space-y-3">
          <ScrollArea
            ref={scrollAreaRef}
            className="min-h-0 flex-1 rounded-md border bg-muted/20 p-4"
          >
            <div className="space-y-4">
              {visibleMessages.length === 0 ? (
                <p className="py-12 text-center text-sm text-muted-foreground">
                  Send a message to start chatting with your model.
                </p>
              ) : (
                visibleMessages.map((m, i) => (
                  <div
                    key={i}
                    className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`w-full rounded-lg px-3 py-2 text-sm break-words shadow-sm sm:w-auto ${
                        m.role === 'user'
                          ? 'max-w-[85%] bg-canada-red text-white'
                          : 'max-w-[min(100%,56rem)] border bg-background'
                      }`}
                    >
                      {m.role === 'assistant' ? (
                        <div className="space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              Assistant
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 shrink-0"
                              onClick={() => void copyMessage(m.content, i)}
                              title="Copy response"
                            >
                              {copiedMessageIndex === i ? (
                                <Check className="h-3.5 w-3.5 text-green-500" />
                              ) : (
                                <Copy className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          </div>
                          <div
                            ref={(node) => {
                              if (node) {
                                messageContentRefs.current.set(i, node);
                              } else {
                                messageContentRefs.current.delete(i);
                              }
                            }}
                            className="prose prose-sm max-w-none break-words dark:prose-invert prose-p:my-2 prose-pre:my-2 prose-ul:my-2 prose-ol:my-2 prose-code:before:content-none prose-code:after:content-none"
                          >
                            <AssistantMarkdown content={m.content} />
                          </div>
                        </div>
                      ) : (
                        <div className="whitespace-pre-wrap break-words">{m.content}</div>
                      )}
                    </div>
                  </div>
                ))
              )}
              {sending && (
                <div className="flex justify-start">
                  <div className="inline-flex items-center gap-2 rounded-lg border bg-background px-3 py-2 text-sm text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-canada-red" />
                    Thinking…
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {queuedMessages.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-border/70 bg-muted/20">
              <div className="flex items-center justify-between gap-2 px-3 py-2">
                <div className="flex min-w-0 items-center gap-2 text-sm">
                  <ListOrdered className="h-4 w-4 shrink-0 text-canada-red" />
                  <span className="font-semibold text-foreground">{queueCountLabel}</span>
                  <span className="truncate text-muted-foreground">- will send after current response</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => stopActiveRun({ clearQueue: true, showToast: true })}
                    title="Stop current response and clear queued messages"
                  >
                    <Square className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => setQueueExpanded((prev) => !prev)}
                    title={queueExpanded ? 'Collapse queue' : 'Expand queue'}
                  >
                    {queueExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {queueExpanded && (
                <div className="space-y-2 border-t border-border/70 px-3 py-3">
                  {queuedMessages.map((queuedMessage, index) => (
                    <div
                      key={`${index}-${queuedMessage}`}
                      className="rounded-lg bg-background px-3 py-2 text-sm break-words"
                    >
                      {queuedMessage}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex items-end gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                selectedJobId
                  ? sending || queuedMessages.length > 0
                    ? 'Type another message to add it to the queue…'
                    : 'Type a message…'
                  : 'Select a fine-tune above to begin'
              }
              disabled={!selectedJobId}
              className="min-h-[60px] resize-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  queueOrSendMessage();
                }
              }}
            />
            <Button
              onClick={queueOrSendMessage}
              disabled={!selectedJobId || !input.trim()}
              className="h-[60px]"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Press Enter to send · Shift+Enter for newline
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
